/**
 * Holding on to a half-finished booking while the visitor goes off to buy
 * something.
 *
 * A page priced in points cannot be booked without a balance, and the place to
 * get one is the host's shop — a different page. Sending someone there used to
 * mean starting over on the way back, which is a poor trade for a purchase we
 * asked them to make.
 *
 * sessionStorage, not localStorage: this belongs to the tab that started the
 * booking and should die with it. A resume note surviving until next week would
 * ambush someone opening a booking link with choices they made and abandoned
 * days ago.
 *
 * It stores a CHOICE, never an entitlement. What they may actually book is
 * resolved from the server on the way back, so a tampered note buys nothing.
 */

const KEY = 'saillab_booking_resume';

export interface BookingResume {
  /** Which booking page this belongs to. Restored to nothing else. */
  meetingTypeId: number;
  /** The service option they had chosen, if any. */
  serviceId?: number | null;
  /** Where to send them back to — the booking page they came from. */
  returnTo: string;
}

export const saveBookingResume = (resume: BookingResume): void => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(resume));
  } catch {
    /* ignore — they simply re-pick on the way back */
  }
};

/**
 * Read WITHOUT consuming.
 *
 * Clearing is a separate call on purpose: the caller can only act on this once
 * its options have actually loaded, and a read that also cleared would discard
 * the note on the render that could not use it yet.
 */
export const readBookingResume = (): BookingResume | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Anything unrecognisable is treated as absent rather than trusted.
    if (!parsed || typeof parsed.meetingTypeId !== 'number') return null;
    return parsed as BookingResume;
  } catch {
    return null;
  }
};

export const clearBookingResume = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};
